import styled from 'styled-components';
import { Facebook, Github, Linkedin, Twitter } from 'styled-icons/fa-brands';
import { Flex, Box } from 'reflexbox/styled-components';
import { Center } from '@bedrock-layout/center';
import Image from 'next/image';
import Link from 'next/link';

import team from '@/data/team.json';
import advisors from '@/data/advisors.json';
import Section from '@/components/section';
import Title from '@/components/title';
import Paragraph from '@/components/paragraph';
import Card from '@/components/card';

const Description = styled.h2`
  font-size: 1.25em;
  font-weight: 300;
  margin-bottom: 4em;
`;

const Member = styled(Card)`
  height: 100%;
  margin: 0 1em;
`;

const Mugshot = styled(Image)`
  border-radius: 50%;
`;

const Name = styled.h2`
  color: ${props => props.theme.colors.brand.primary};
  font-size: 1.25em;
  font-weight: 400;
  margin-bottom: .25em;
`;

const Role = styled.h4`
  color: ${props => props.theme.colors.typography.subtle};
  font-size: 1em;
  font-weight: 400;
  margin-top: 0;
`;

const Bio = styled(Paragraph)`
  margin-top: 0;
`;

const SocialLinks = styled.ul`
  align-items: center;
  display: flex;
  list-style: none;
  padding: 0;
  margin: 1em 0;

  a, a:hover {
    color: ${props => props.theme.colors.typography.verySubtle};
    text-decoration: none;
  }

  li + li {
    margin-left: 1em;
  }

  svg {
    height: 1.5em;
    width: 1.5em;
  }
`;

const Team = () => {
  const icons = {
    facebook: <Facebook />,
    github: <Github />,
    linkedin: <Linkedin />,
    twitter: <Twitter />
  };

  return (
    <Section style={{ paddingTop: 0 }}>
      <Center centerText>
        <Title>Our team</Title>
        <Description>Collectively we&apos;ve built dozens of products for startups to Fortune 100s. We ❤️ open source and built one of the world&apos;s most popular real-time Node.js web development frameworks, <a href="https://blog.feathersjs.com/flying-into-2018-13bda623f089" target="_blank" title="FeathersJS 2017 recap" rel="noopener noreferrer">used all over the world</a>.</Description>
      </Center>
      <Flex flexWrap="wrap" mx="-1em">
        {team.map((member, index) => (
          <Box key={index} width={[1, 1/2, 1/2, 1/4]} mb="2em">
            <Member>
              <Center centerText centerChildren>
                <Mugshot height={128} src={member.mugshot} width={128} />
                <Name>{member.name}</Name>
                <Role>{member.role}</Role>
                <Bio>{member.bio}</Bio>
                <SocialLinks>
                  {Object.keys(member.social).map(network => (
                    <li key={network}>
                      <Link href={member.social[network]}>
                        <a>{icons[network]}</a>
                      </Link>
                    </li>
                  ))}
                </SocialLinks>
              </Center>
            </Member>
          </Box>
        ))}
      </Flex>
      <Center centerText>
        <Title>Investors & Advisors</Title>
      </Center>
      <Flex flexWrap="wrap" mx="-1em">
        {advisors.map((advisor, index) => (
          <Box key={index} width={[1, 1/2]} mb="2em">
            <Member>
              <Center centerText centerChildren>
                <Mugshot height={128} src={advisor.mugshot} width={128} />
                <Name>{advisor.name}</Name>
                <Role>{advisor.role}</Role>
                <Bio>{advisor.bio}</Bio>
                <SocialLinks>
                  {Object.keys(advisor.social).map(network => (
                    <li key={network}>
                      <Link href={advisor.social[network]}>
                        <a>{icons[network]}</a>
                      </Link>
                    </li>
                  ))}
                </SocialLinks>
              </Center>
            </Member>
          </Box>
        ))}
      </Flex>
    </Section>
  );
};

export default Team;
